/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";
import { XModal } from "../../component";
import useAxios from "../../hooks/useAxios";
import { API_ENDPOINTS } from "../../constants/api";
import SingleAnalyticsUI from "../../component/singleAnalyticsUI/SingleAnalyticsUI";
import moment from "moment";

interface SingleCampaignAnalyticsProps {
  selectedCampaignId: number;
  setSelectedCampaignId: any;
}

const SingleCampaignAnalytics: React.FC<SingleCampaignAnalyticsProps> = ({
  selectedCampaignId,
  setSelectedCampaignId,
}) => {
  const [campaignAnalyticsModalShow, setCampaignAnalyticsModalShow] = useState<boolean>(false);
  const [analyticsData, setAnalyticsData] = useState<any>([]);
  const [rawOccurrenceData, setRawOccurrenceData] = useState<any>(null); // NEW: holds recurring response as-is
  const [selectedOccurrence, setSelectedOccurrence] = useState<string>(""); // NEW
  const { postData, isLoading } = useAxios();
  const { orgLocations, selectedLocationId } = useSelector((state: RootState) => state.user);

  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);

  const locationTimezone = activeLocation?.location_timezone || 'UTC';

  const formatOccurrenceDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: locationTimezone,
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(d);
    } catch (e) {
      return moment(dateStr).format("dddd, MMMM Do YYYY");
    }
  };

  useEffect(() => {
    if (selectedCampaignId) {
      setCampaignAnalyticsModalShow(true);
      fetchAnalytics(selectedCampaignId);
    }
  }, [selectedCampaignId]);

  const fetchAnalytics = async (campaignId: any) => {
    const response = await postData(
      API_ENDPOINTS.CAMPAIGN_ANALYTICS_DETAILS_BY_ID,
      { campaignId }
    );

    if (response?.data?.isRecurring) {
      // NEW: recurring campaign - store raw data, default to latest occurrence
      const occurrences = response?.data?.occurrences || [];
      const campaign = response?.data?.campaign;
      setRawOccurrenceData({ campaign, occurrences });
      const latest = occurrences[0]?.occurrence_date || "";
      setSelectedOccurrence(latest);
      applyOccurrence(campaign, occurrences, latest);
    } else {
      // UNCHANGED: exact old flow for one-time campaigns
      setRawOccurrenceData(null);
      setSelectedOccurrence("");
      setAnalyticsData({
        analytics: response?.data?.selectedCampaignAnalytics,
        contacts: response?.data?.selectedCampaignContacts,
      });
    }
  };

  // NEW: reshapes one occurrence's data into the exact shape SingleAnalyticsUI already expects
  const applyOccurrence = (campaign: any, occurrences: any[], occurrenceDate: string) => {
    const occurrence = occurrences.find((o: any) => o.occurrence_date === occurrenceDate);
    if (!occurrence) {
      setAnalyticsData({ analytics: [], contacts: [] });
      return;
    }

    const analytics = [{
      title: campaign?.title,
      type: campaign?.type,
      contact_count: occurrence.contacts.length,
      open: occurrence.stats.open,
      click: occurrence.stats.click,
      hard_bounce: occurrence.stats.hard_bounce_rate,
      soft_bounce: occurrence.stats.soft_bounce_rate,
    }];

    const contacts = occurrence.contacts.map((log: any) => ({
      id: log.id,
      email: log.recipient,
      campaign_logs: [log],
    }));

    setAnalyticsData({ analytics, contacts });
  };

  const handleOccurrenceChange = (occurrenceDate: string) => {
    setSelectedOccurrence(occurrenceDate);
    if (rawOccurrenceData) {
      applyOccurrence(rawOccurrenceData.campaign, rawOccurrenceData.occurrences, occurrenceDate);
    }
  };

  return (
    <XModal
      show={campaignAnalyticsModalShow}
      handleClose={() => {
        setCampaignAnalyticsModalShow(false);
        setSelectedCampaignId("");
      }}
      title={"Campaign Analytics"}
      confirmText={"Close"}
      handleConfirm={() => {
        setCampaignAnalyticsModalShow(false);
        setSelectedCampaignId("");
      }}
      className="wide-modal-dialog"
      body={
        <>
          {rawOccurrenceData && rawOccurrenceData.occurrences.length > 0 && (
            <Form.Group className="mb-3" style={{ maxWidth: "260px" }}>
              <Form.Label style={{ fontSize: "13px" }}>Select occurrence</Form.Label>
              <Form.Select
                value={selectedOccurrence}
                onChange={(e) => handleOccurrenceChange(e.target.value)}
              >
                {rawOccurrenceData.occurrences.map((occ: any) => (
                  <option key={occ.occurrence_date} value={occ.occurrence_date}>
                    {formatOccurrenceDate(occ.occurrence_date)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <SingleAnalyticsUI
            data={analyticsData}
            title={"Campaign Name"}
            type={"Campaign Type"}
            loader={isLoading}
          />
        </>
      }
    />
  );
};

export default SingleCampaignAnalytics;
